import React from "react";
import compose from "ramda/src/compose";
import mergeRight from "ramda/src/mergeRight";
import { Controller, useForm } from "react-hook-form";

import { useModalContext } from "../../contexts/ModalContext/context";
import { Button } from "../../UI/Button";
import { Grid } from "../../UI/Grid";
import { ModalWithoutOverflow } from "../ModalWithoutOverflow";
import { getControllerTextFieldRender } from "../../helpers/getControllerTextFieldRender";
import {
  ReportSubscription,
  updateReportSubscription,
} from "../../services/AdminApi";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import useAuth from "../../routes/login/AuthContext";
import { useSnackbar } from "../../hooks/useSnackbar";

interface IFormData {
  managerName: string;
  managerEmail: string;
}

interface IProps {
  subscription: ReportSubscription;
  reloadSubscriptions: () => Promise<void>;
}

export const EditAdminNotificationModal: React.FC<IProps> = (props) => {
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [isLoading, setLoading] = React.useState(false);
  const { setIsOpen } = useModalContext();
  const { getAuthToken } = useAuth();
  const {
    formState: { errors },
    control,
    handleSubmit,
  } = useForm<IFormData>({
    defaultValues: {
      managerName: props.subscription.managerName,
      managerEmail: props.subscription.managerEmail,
    },
  });

  const handleHide = () => setIsOpen(false);

  const handleUpdate = async (data: IFormData) => {
    setLoading(true);

    const success = await updateReportSubscription(
      selectedNetworkId,
      {
        ...props.subscription,
        managerName: data.managerName,
        managerEmail: data.managerEmail,
      },
      await getAuthToken(),
    );

    if (success) {
      showSuccessSnackbar("Saved Changes");
      await props.reloadSubscriptions();
      handleHide();
    } else {
      showErrorSnackbar("An Error Occurred");
    }

    setLoading(false);
  };

  const getDefaultInputProps = mergeRight({ errors });

  const getTextControllerRender = React.useCallback(
    // @ts-ignore
    compose(getControllerTextFieldRender, getDefaultInputProps),
    [],
  );

  return (
    <ModalWithoutOverflow
      maxWidth="md"
      onClose={handleHide}
      title="Edit Email"
      actions={
        <>
          <Button appearance="subtle" onClick={handleHide}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSubmit(handleUpdate)}
            isLoading={isLoading}
          >
            Save
          </Button>
        </>
      }
    >
      <Grid container spacing={2} px={3} py={2} width={440}>
        <Grid item xs={12}>
          <Controller
            name="managerName"
            control={control}
            // @ts-ignore
            render={getTextControllerRender({ label: "Name", required: true })}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="managerEmail"
            control={control}
            // @ts-ignore
            render={getTextControllerRender({ label: "Email", required: true })}
          />
        </Grid>
      </Grid>
    </ModalWithoutOverflow>
  );
};
